import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useFlash } from "../lib/flash";
import { formatKr, fmtSlots, fmtDateTime, toLocalInput, fromLocalInput } from "../lib/format";
import SlotEditor, { slotsToRows, rowsToSlots, rowsError } from "../components/SlotEditor";

function Media({ s }) {
  if (!s.media.length) return s.image_path ? <img className="rp-img" src={s.image_path} alt="" /> : null;
  return (
    <div className="rp-media" data-testid="repost-media">
      {s.media.map((m) => (m.kind === "video" ? <video key={m.id} src={m.url} poster={m.thumb_url} controls playsInline /> : <img key={m.id} src={m.url} alt="" />))}
    </div>
  );
}

export default function RepostReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const flash = useFlash();
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [salesEnd, setSalesEnd] = useState(null);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    api.get(`/reposts/${id}`).then((r) => {
      setS(r.data);
      setText(r.data.text);
      setRows(r.data.pickup_slots.length ? slotsToRows(r.data.pickup_slots) : [{ date: "", from: "", to: "" }]);
      setSalesEnd(r.data.sales_end);
    }).catch((e) => setErr(apiError(e.response?.data?.detail) || "Forslaget findes ikke."));
  }, [id]);

  const go = async (kind) => {
    let body = null;
    if (kind === "approve") {
      const slotErr = rowsError(rows);
      if (slotErr) return setErrors([slotErr]);
      body = { text, pickup_slots: rowsToSlots(rows), sales_end: salesEnd || null };
    }
    setBusy(true);
    setErrors([]);
    try {
      const r = await api.post(`/reposts/${id}/${kind}`, body);
      flash(r.data.flash);
      nav(kind === "approve" ? `/offers/${r.data.id}` : "/overblik");
    } catch (e) {
      const list = e.response?.data?.errors;
      if (list?.length) setErrors(list);
      else flash(apiError(e.response?.data?.detail) || e.message, "error");
    } finally { setBusy(false); }
  };

  if (err) return <div className="notice error" data-testid="repost-error">{err} <Link to="/overblik">Til overblik</Link></div>;
  if (!s) return null;
  const open = s.status === "pending";
  return (
    <>
      <div className="page-head">
        <div>
          <h1 data-testid="repost-title">Klar til genopslag</h1>
          <p>Genopslag {s.n}/{s.max} af «{s.root_title}» · forslaget udløber {fmtDateTime(s.expires_at)}</p>
        </div>
      </div>
      {!open && <div className="notice" data-testid="repost-closed">Forslaget er ikke længere aktivt.</div>}
      <div className="grid cols-main">
        <section className="panel" data-testid="repost-panel">
          <h2>{s.title}</h2>
          <div className="field">
            <label htmlFor="repost-text">Tekst til genopslaget</label>
            <textarea id="repost-text" rows={6} maxLength={1500} value={text} onChange={(e) => setText(e.target.value)} disabled={!open} data-testid="repost-text" />
          </div>
          <dl className="rp-facts">
            <dt>Nyt lager</dt><dd data-testid="repost-stock">{s.remaining} stk.</dd>
            {s.variants && (<><dt>{s.variant_label || "Varianter"}</dt><dd data-testid="repost-variants">{s.variants.map((v) => `${v.name}: ${v.qty}`).join(" · ")}</dd></>)}
            <dt>Ja tak-pris</dt><dd data-testid="repost-price">{formatKr(s.price_ore)}</dd>
            {!open && (<><dt>Afhentning</dt><dd data-testid="repost-pickup">{s.pickup_slots.length ? fmtSlots(s.pickup_slots) : "Kunne ikke flyttes"}</dd></>)}
            {!open && s.sales_end && (<><dt>Bestil senest</dt><dd data-testid="repost-deadline">{fmtDateTime(s.sales_end)}</dd></>)}
          </dl>
          {open && (
            <div className="rp-times" data-testid="repost-times">
              <h3 style={{ marginTop: 0 }}>Afhentning</h3>
              {!s.pickup_slots.length && <div className="notice" data-testid="repost-shift-failed">Afhentningsdagene kunne ikke flyttes automatisk – angiv dem her.</div>}
              <SlotEditor rows={rows} onChange={setRows} />
              <div className="row dates">
                <div className="field">
                  <label htmlFor="repost_sales_end">Bestil senest</label>
                  <input id="repost_sales_end" type="datetime-local" value={toLocalInput(salesEnd)} onChange={(e) => setSalesEnd(fromLocalInput(e.target.value))} data-testid="repost-sales-end" />
                  <div className="hint">Tom = indtil afhentningen slutter.</div>
                </div>
              </div>
              <p className="hint">Forslaget er flyttet lige så mange dage frem, som der er gået siden det første opslag. Ret dagene, hvis de ikke passer.</p>
            </div>
          )}
          {errors.length > 0 && (
            <div className="notice error" role="alert" data-testid="repost-errors">{errors.map((x) => <div key={x}>{x}</div>)}</div>
          )}
          {open && (
            <div className="actions rp-actions">
              <button className="btn" disabled={busy} onClick={() => go("reject")} data-testid="repost-reject-btn">Afvis</button>
              <button className="btn jatak" disabled={busy || !text.trim()} onClick={() => go("approve")} data-testid="repost-approve-btn">Godkend og slå op</button>
            </div>
          )}
        </section>
        <aside className="panel"><h2>Billeder eller video</h2>{s.media.length || s.image_path ? <Media s={s} /> : <p className="muted">Tilbuddet har ingen billeder.</p>}</aside>
      </div>
    </>
  );
}

export function RepostCards({ items }) {
  if (!items?.length) return null;
  return (
    <section className="panel repost-cards" data-testid="repost-cards">
      <h2>Klar til genopslag</h2>
      {items.map((s) => (
        <Link key={s.id} to={`/genopslag/${s.id}`} className="rp-card" data-testid={`repost-card-${s.id}`}>
          {s.image_path && <img src={s.image_path} alt="" />}
          <span className="rp-card-text"><b>{s.title}</b><br /><span className="muted">{s.remaining} tilbage · genopslag {s.n}/{s.max}</span></span>
          <span className="btn small rp-card-btn">Se forslag</span>
        </Link>
      ))}
    </section>
  );
}
